import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from './Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUser, faEnvelope, faPhone, faBuilding, faMessage, faCheck, faExclamationTriangle, faPhoneVolume, faSpinner } from '@fortawesome/free-solid-svg-icons';

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: null,
    type: null, // 'form' or 'call'
    message: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Immediately show loading state
    setStatus({ 
      loading: true, 
      success: false, 
      error: null, 
      type: 'form',
      message: 'Sending your message...'
    });

    // Simulate loading for 1.5 seconds
    await new Promise(resolve => setTimeout(resolve, 1500));

    // Always show success
    setStatus({ 
      loading: false, 
      success: true, 
      error: null, 
      type: 'form',
      message: 'Thank you for your message! We\'ll get back to you soon.'
    });
    
    // Reset form after successful submission
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      message: ''
    });

    // Close modal after 3 seconds on success
    setTimeout(() => {
      onClose();
      setStatus({ 
        loading: false, 
        success: false, 
        error: null, 
        type: null,
        message: null 
      });
    }, 3000);
  };

  const handleCallClick = (e) => {
    e.preventDefault();
    setStatus({ 
      loading: false, 
      success: true, 
      error: null, 
      type: 'call',
      message: 'Initiating call...'
    });
    
    // Wait 1.5 seconds before triggering the call
    setTimeout(() => {
      window.location.href = 'tel:402-731-9300';
    }, 1500);
    
    // Close modal after 2 seconds
    setTimeout(() => {
      onClose();
      setStatus({ 
        loading: false, 
        success: false, 
        error: null, 
        type: null,
        message: null 
      });
    }, 2000);
  };

  const handleInputClick = (e) => {
    e.stopPropagation();
  };

  const renderContent = () => {
    if (status.loading || status.success) {
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center justify-center py-8"
        >
          <div className="w-16 h-16 bg-[#D3242A]/10 rounded-full flex items-center justify-center mb-4">
            {status.loading ? (
              <FontAwesomeIcon 
                icon={faSpinner} 
                className="text-[#D3242A] text-2xl animate-spin" 
              />
            ) : status.type === 'form' ? (
              <FontAwesomeIcon icon={faCheck} className="text-[#D3242A] text-2xl" />
            ) : (
              <motion.div
                animate={{
                  rotate: [-10, 10, -10, 10, 0],
                }}
                transition={{
                  duration: 1,
                  ease: "easeInOut",
                  repeat: 2,
                }}
              >
                <FontAwesomeIcon icon={faPhoneVolume} className="text-[#D3242A] text-2xl" />
              </motion.div>
            )}
          </div>
          <p className="text-[#D3242A] font-medium text-lg">
            {status.message}
          </p>
        </motion.div>
      );
    }

    return (
      <motion.form
        onSubmit={handleSubmit}
        className="space-y-3"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        onClick={handleInputClick}
      >
        {status.error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#D3242A]/10 border border-[#D3242A]/20 text-[#D3242A] px-4 py-2 rounded-lg text-sm flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faExclamationTriangle} />
            {status.error}
          </motion.div>
        )}

        <div className="grid grid-cols-1 gap-3">
          <div className="relative">
            <FontAwesomeIcon 
              icon={faUser} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Your Name"
              className="w-full pl-10 pr-3 py-2 rounded-full border border-gray-300 focus:border-[#D3242A] focus:ring-2 focus:ring-[#D3242A]/20 outline-none transition-all text-sm"
              disabled={status.loading}
            />
          </div>

          <div className="relative">
            <FontAwesomeIcon 
              icon={faEnvelope} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Your Email"
              className="w-full pl-10 pr-3 py-2 rounded-full border border-gray-300 focus:border-[#D3242A] focus:ring-2 focus:ring-[#D3242A]/20 outline-none transition-all text-sm"
              disabled={status.loading}
            />
          </div>

          <div className="relative">
            <FontAwesomeIcon 
              icon={faPhone} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Your Phone"
              className="w-full pl-10 pr-3 py-2 rounded-full border border-gray-300 focus:border-[#D3242A] focus:ring-2 focus:ring-[#D3242A]/20 outline-none transition-all text-sm"
              disabled={status.loading}
            />
          </div>

          <div className="relative">
            <FontAwesomeIcon 
              icon={faBuilding} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Your Company"
              className="w-full pl-10 pr-3 py-2 rounded-full border border-gray-300 focus:border-[#D3242A] focus:ring-2 focus:ring-[#D3242A]/20 outline-none transition-all text-sm"
              disabled={status.loading}
            />
          </div>

          <div className="relative">
            <FontAwesomeIcon 
              icon={faMessage} 
              className="absolute left-3 top-3 text-gray-400"
            />
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              placeholder="Your Message"
              rows={2}
              className="w-full pl-10 pr-3 py-2 rounded-xl border border-gray-300 focus:border-[#D3242A] focus:ring-2 focus:ring-[#D3242A]/20 outline-none transition-all resize-none text-sm"
              disabled={status.loading}
            />
          </div>
        </div>

        <motion.div
          className="flex flex-col sm:flex-row justify-center gap-3 mt-4"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <button
            type="submit"
            className="inline-flex items-center justify-center rounded-full font-bold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 cursor-pointer select-none bg-[#D3242A] text-white hover:bg-white hover:text-[#D3242A] hover:border-2 hover:border-[#D3242A] focus:ring-[#D3242A] px-4 py-2 text-sm w-full sm:w-auto"
            disabled={status.loading}
          >
            {status.loading ? (
              <span className="flex items-center gap-2">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                Sending...
              </span>
            ) : 'Submit'}
          </button>
          <a
            href="tel:402-731-9300"
            onClick={handleCallClick}
            className="inline-flex items-center justify-center rounded-full font-bold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 cursor-pointer select-none bg-white text-[#D3242A] border-2 border-[#D3242A] hover:bg-[#D3242A] hover:text-white px-4 py-2 text-sm w-full sm:w-auto"
          >
            Call Now
          </a>
        </motion.div>
      </motion.form>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-[3000] flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-lg p-6 max-w-sm w-full relative"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 transition-colors"
              aria-label="Close modal"
            >
              <FontAwesomeIcon icon={faTimes} className="text-lg" />
            </button>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <motion.h2
                className="text-2xl font-bold text-gray-900 mb-4"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                Contact Us
              </motion.h2>

              {renderContent()}
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContactModal; 